import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import './StartSubmission.css';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS, getAuthHeaders } from '../../../../config/api.config';
import imageCompression from 'browser-image-compression';

const QuestionView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { classId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [isAnswerRequired, setIsAnswerRequired] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [extractedText, setExtractedText] = useState('');
  const [processingImage, setProcessingImage] = useState(false);
  const [showAnswerSection, setShowAnswerSection] = useState(false);
  const [selectedInputMethod, setSelectedInputMethod] = useState(null);
  const [submissionSaved, setSubmissionSaved] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  
  // New state for image confirmation
  const [isConfirmingImages, setIsConfirmingImages] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [zoomImg, setZoomImg] = useState(null);

  // Get class name and teacher name from location state or localStorage
  const className = location.state?.className || 
    JSON.parse(localStorage.getItem('classes') || '[]')
      .find(c => c._id === classId)?.name || 
    'Class';
  
  const teacherName = location.state?.teacherName || 
    JSON.parse(localStorage.getItem('classes') || '[]')
      .find(c => c._id === classId)?.teacher || 
    'Not assigned';

  useEffect(() => {
    const role = localStorage.getItem('role');
    if (role === 'teacher') {
      navigate('/dashboard');
      return;
    }

    fetchQuestions();
  }, [classId, navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(
        getApiUrl(API_ENDPOINTS.CLASS.QUESTIONS(classId)),
        { headers: getAuthHeaders() }
      );
      
      if (response.data && response.data.success) {
        setQuestions(response.data.questions || []);
      } else {
        setError("No questions found for this class");
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      setError("Failed to load questions. Please try again.");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const compressImage = async (imageFile) => {
    const options = {
      maxSizeMB: 9.5,          // Slightly under the 10MB limit to be safe
      maxWidthOrHeight: 2048,   // Increased max dimension while maintaining aspect ratio
      useWebWorker: true,
      initialQuality: 0.8       // Start with good quality
    };
    
    try {
      const compressedFile = await imageCompression(imageFile, options);
      
      // If still too large, compress further
      if (compressedFile.size > 9.5 * 1024 * 1024) {
        options.maxSizeMB = 5;
        options.initialQuality = 0.7;
        return await imageCompression(compressedFile, options);
      }
      
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      throw error;
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:image/jpeg;base64, prefix
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const currentTotal = pendingImages.length + files.length;
    
    if (currentTotal > 5) {
      setError(`You can upload a maximum of 5 images total. Currently have ${pendingImages.length} images.`);
      alert('Maximum 5 images allowed');
      event.target.value = '';
      return;
    }

    // Validate file sizes
    const tooLarge = files.some(f => f.size > 5 * 1024 * 1024);
    if (tooLarge) {
      setError('Each image must be under 5 MB');
      event.target.value = '';
      return;
    }

    try {
      setError('');

      const newImages = [];
      const newPreviews = [];

      // Process images for preview only
      for (const file of files) {
        const compressed = await compressImage(file);
        newImages.push(compressed);

        // Create preview
        const previewReader = await new Promise((resolve) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.readAsDataURL(compressed);
        });
        newPreviews.push(previewReader);
      }

      // Add new images to pending arrays
      setPendingImages([...pendingImages, ...newImages]);
      setPendingPreviews([...pendingPreviews, ...newPreviews]);
      setIsConfirmingImages(true);
      
      // Clear the input
      event.target.value = '';
    } catch (err) {
      console.error('Error processing images', err);
      setError('Error processing images. Please try again.');
    }
  };

  const handleAddMoreImages = () => {
    // Trigger file input click
    const fileInput = document.getElementById('additional-images-input');
    fileInput?.click();
  };

  const handleRemoveImage = (index) => {
    const updatedImages = pendingImages.filter((_, i) => i !== index);
    const updatedPreviews = pendingPreviews.filter((_, i) => i !== index);
    
    setPendingImages(updatedImages);
    setPendingPreviews(updatedPreviews);
    
    // If no images left, exit confirmation mode
    if (updatedImages.length === 0) {
      setIsConfirmingImages(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newImages = [...pendingImages];
    const newPreviews = [...pendingPreviews];
    
    // Remove dragged items
    const draggedImage = newImages.splice(draggedIndex, 1)[0];
    const draggedPreview = newPreviews.splice(draggedIndex, 1)[0];
    
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);
    newPreviews.splice(dropIndex, 0, draggedPreview);
    
    setPendingImages(newImages);
    setPendingPreviews(newPreviews);
    setDraggedIndex(null);
  };

  const handleConfirmImages = async () => {
    if (pendingImages.length === 0) return;

    try {
      setProcessingImage(true);
      setError('');

      let combinedRaw = '';

      // Extract text from confirmed images in order
      for (const image of pendingImages) {
        // Convert to base64
        const base64String = await convertToBase64(image);

        // Get text from Textract
        const textractResponse = await axios.post(
          getApiUrl(API_ENDPOINTS.UPLOAD),
          { image: base64String },
          {
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            }
          }
        );

        if (textractResponse.data.status === "successful") {
          const rawText = textractResponse.data.data.formData;
          combinedRaw += (combinedRaw ? '\n\n' : '') + rawText;
        }
      }

      // Cleanup combined text via Claude
      let cleaned = combinedRaw;
      try {
        const claudeResp = await axios.post(
          getApiUrl('/api/claude/cleanup-text'),
          { text: combinedRaw },
          {
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            }
          }
        );
        if (claudeResp.data.status === 'success') {
          cleaned = claudeResp.data.data.cleanedText;
        }
      } catch (e) {
        console.error('Claude cleanup error', e);
      }

      // Set final images and text
      setCurrentAnswer(cleaned);
      setShowAnswerSection(true);
      setSelectedImages([...selectedImages, ...pendingImages]);
      setPreviewUrls([...previewUrls, ...pendingPreviews]);
      
      // Exit confirmation mode
      setIsConfirmingImages(false);
      setPendingImages([]);
      setPendingPreviews([]);
    } catch (err) {
      console.error('Error extracting text', err);
      setError('Error extracting text. Please try again.');
    } finally {
      setProcessingImage(false);
    }
  };

  const handleCancelImages = () => {
    setPendingImages([]);
    setPendingPreviews([]);
    setIsConfirmingImages(false);
    setError('');
  };

  const handleCameraCapture = () => {
    navigate('/takeImage', {
      state: {
        returnTo: `/start-submission/${classId}`,
        currentQuestionIndex,
        className,
        onImageCapture: async (imageFiles) => {
          const filesArr = Array.isArray(imageFiles) ? imageFiles : [imageFiles];
          if (filesArr.length + selectedImages.length > 5) {
            alert('Maximum 5 images allowed');
            return;
          }
          setProcessingImage(true);

          try {
            const newSel = [];
            const newPrev = [];
            let comboRaw = '';

            for (const img of filesArr) {
              const compressedImage = await compressImage(img);
              newSel.push(compressedImage);

              const prevUrl = await new Promise((res) => { const r=new FileReader(); r.onload=()=>res(r.result); r.readAsDataURL(compressedImage); });
              newPrev.push(prevUrl);

              // Convert compressed image to base64
              const base64String = await convertToBase64(compressedImage);

              // First get text from Textract
              const textractResponse = await axios.post(
                getApiUrl(API_ENDPOINTS.UPLOAD),
                { image: base64String },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                  }
                }
              );

              if (textractResponse.data.status === 'successful') {
                comboRaw += (comboRaw ? '\n\n' : '') + textractResponse.data.data.formData;
              }
            }

            // cleanup once
            let cleanedCam = comboRaw;
            try {
              const claudeResp = await axios.post(getApiUrl('/api/claude/cleanup-text'), { text: comboRaw }, { headers: { 'Content-Type':'application/json', ...getAuthHeaders() } });
              if (claudeResp.data.status==='success') cleanedCam = claudeResp.data.data.cleanedText;
            } catch(e){console.error('Claude cleanup err',e);}

            setCurrentAnswer(cleanedCam);
            setShowAnswerSection(true);
            setSelectedImages(prev=>[...prev,...newSel]);
            setPreviewUrls(prev=>[...prev,...newPrev]);
          } catch (error) {
            console.error('Error processing image:', error);
            setError('Failed to process image. Please try again.');
          } finally {
            setProcessingImage(false);
          }
        }
      }
    });
  };

  const handleBack = () => {
    navigate(`/class-details/${classId}`);
  };

  const handlePrevious = () => {
    setCurrentQuestionIndex(prev => Math.max(0, prev - 1));
    setCurrentAnswer(answers[questions[currentQuestionIndex - 1]?._id] || '');
  };

  const handleNext = async () => {
    if (!currentAnswer.trim()) {
      setIsAnswerRequired(true);
      return;
    }

    const currentQuestion = questions[currentQuestionIndex];
    const newAnswers = { ...answers, [currentQuestion.id || currentQuestion._id]: currentAnswer };
    setAnswers(newAnswers);

    if (currentQuestionIndex === questions.length - 1) {
      setSubmissionSaved(true); // Show saving feedback
      try {
        // Build payload fresh – guarantee questionId present
        const submissionData = {
          classId,
          submissions: questions.map(q => ({
            questionId: q.id || q._id,
            answer: newAnswers[q.id || q._id] || 'No answer provided'
          })),
          totalScore: 0,
          status: 'pending'
        };

        console.log('Submitting data:', JSON.stringify(submissionData, null, 2));

        const response = await axios.post(
          getApiUrl(API_ENDPOINTS.SUBMISSION.CREATE),
          submissionData,
          { headers: getAuthHeaders() }
        );

        console.log('Submission response:', response.data);

        // Navigate to the submission complete page
        setTimeout(() => {
          navigate(`/my-submissions/${response.data.data._id}`);
        }, 2000);

      } catch (error) {
        console.error('Error saving submission:', error);
        setError(error.response?.data?.message || error.message || 'Failed to save submission. Please try again.');
        setSubmissionSaved(false);
      }
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      // Set current answer to the saved answer for the next question if it exists
      const nextQuestion = questions[currentQuestionIndex + 1];
      setCurrentAnswer(answers[nextQuestion?.id || nextQuestion?._id] || '');
      setShowAnswerSection(false); // Reset the answer section for the next question
    }
  };

  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const totalMarks = questions.reduce((sum, q) => sum + (q.marks || 0), 0);

  return (
    <>
      <div className="submission-container">
      {submissionSaved && (
        <div className="submission-overlay">
          <div className="submission-feedback">
            Saving your submission...
          </div>
        </div>
      )}
      
      <div className="class-name-header">
        <h2>{className}</h2>
      </div>

      <div className="question-container">
        <div className="total-info">
          <div>Total Questions: {questions.length}</div>
        </div>

        <div className="question-item">
          <div className="question-header">
            <span>Question {currentQuestionIndex + 1}</span>
            <span className="marks">{currentQuestion?.marks} marks</span>
          </div>
          <div className="question-content">
            <h3>Question {currentQuestionIndex + 1}</h3>
            <p>{questions[currentQuestionIndex]?.question}</p>
            <p className="marks">({questions[currentQuestionIndex]?.marks} marks)</p>
          </div>

          <div className="notification-alert">
            <div className="alert alert-warning" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Please write your answers in pen to ensure better text recognition.
            </div>
          </div>
        </div>

        <div className="answer-label">
          Answer: <span className="pen-warning">(Please write in pen to ensure better text recognition)</span>
        </div>
        
        {!isConfirmingImages && previewUrls.length === 0 && (
          <div className="input-options">
            <div 
              className="input-option"
              onClick={() => {
                setSelectedInputMethod('camera');
                document.getElementById('image-upload').click();
              }}
            >
              <i>📸</i>
              <span>Take Photo</span>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                capture="environment"
              />
            </div>

            <div 
              className="input-option"
              onClick={() => {
                setSelectedInputMethod('upload');
                document.getElementById('image-upload-gallery').click();
              }}
            >
              <i>📁</i>
              <span>Upload Image</span>
              <input
                id="image-upload-gallery"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>

            <div 
              className="input-option"
              onClick={() => {
                setSelectedInputMethod('text');
                setShowAnswerSection(true);
              }}
            >
              <i>✏️</i>
              <span>Type Answer</span>
            </div>
          </div>
        )}

        {isConfirmingImages && (
          <div className="image-confirmation-section">
            <div className="confirmation-header">
              <h4>Confirm Image Order ({pendingImages.length}/5)</h4>
              <p>Drag and drop to reorder images. Click ✕ to remove an image.</p>
            </div>
            
            <div className="image-confirmation-grid">
              {pendingPreviews.map((url, index) => (
                <div
                  key={index}
                  className={`image-confirmation-item ${draggedIndex === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <div className="image-number">{index + 1}</div>
                  <img 
                    src={url} 
                    alt={`Preview ${index + 1}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setZoomImg(url);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(index);
                    }}
                    title="Remove image"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div className="confirmation-actions">
              {pendingImages.length < 5 && (
                <button
                  type="button"
                  className="add-more-btn"
                  onClick={handleAddMoreImages}
                >
                  + Add More Images ({5 - pendingImages.length} remaining)
                </button>
              )}
              
              <div className="confirmation-buttons">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancelImages}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="confirm-btn"
                  onClick={handleConfirmImages}
                  disabled={pendingImages.length === 0}
                >
                  Confirm & Extract Text
                </button>
              </div>
            </div>

            {/* Hidden input for adding more images */}
            <input
              id="additional-images-input"
              type="file"
              onChange={handleImageUpload}
              accept="image/*"
              multiple
              style={{ display: 'none' }}
            />
          </div>
        )}
        
        {previewUrls.length > 0 && !isConfirmingImages && (
          <div className="preview-section">
            <div className="preview-header">
              <label>Final Images ({previewUrls.length}):</label>
              <button 
                type="button" 
                className="replace-images-btn"
                onClick={() => {
                  setSelectedImages([]);
                  setPreviewUrls([]);
                  setCurrentAnswer('');
                  setShowAnswerSection(false);
                }}
              >
                Replace Images
              </button>
            </div>
            <div className="image-preview image-preview-multi">
              {previewUrls.map((url, idx) => (
                <div key={idx} className="final-image-item">
                  <div className="final-image-number">{idx + 1}</div>
                  <img
                    src={url}
                    alt={`Final image ${idx + 1}`}
                    onClick={() => setZoomImg(url)}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`answer-section ${showAnswerSection ? 'visible' : ''}`}>
          <textarea
            value={currentAnswer}
            onChange={(e) => {
              setCurrentAnswer(e.target.value);
              setIsAnswerRequired(false);
            }}
            placeholder="Type your answer here..."
          />
          {isAnswerRequired && (
            <div className="error-message">Please provide an answer</div>
          )}
        </div>

        {processingImage && (
          <div className="processing-message">
            Processing image...
          </div>
        )}

        <div className="navigation-buttons">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="nav-button"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            className="nav-button primary"
          >
            {currentQuestionIndex === questions.length - 1 ? 'Submit' : 'Next'}
          </button>
        </div>
      </div>
      </div>
      
      {/* Modal rendered outside the container for true fullscreen */}
      {zoomImg && (
        <div className="image-modal" onClick={() => setZoomImg(null)}>
          <img src={zoomImg} alt="zoomed" />
        </div>
      )}
    </>
  );
};

export default QuestionView; 